import React, { useEffect, useState, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import WidgetCard from "../../Components/WidgetCard";
import TextboxSet from "../../Components/TextboxSet";
import SeatManagementTable from "../../Components/Tables/SeatManagementTable";
import { useToast } from "../../Components/Toast";
import axios from "axios";
import {
  CREATE_TABLE_API,
  GET_AVAILABILITY_COUNT_API,
  GET_SEATS_WIDGET_API,
  GET_TABLE_DETAIL_LIST_API,
} from "../../Constant/api";
import Modal from "../../Components/Modal";
import Button from "../../Components/Button";
import Counter from "../../Components/Counter";
import Breadcrumb from "../../Components/Breadcrumb";
import Can from "../../Components/Can";
import Switch from "react-switch";

const SeatManagementList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableDetailData, setTableDetailData] = useState();
  const [tableDetailMeta, setTableDetailMeta] = useState();
  const [widgetData, setWidgetData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    pax: 10,
    is_multi: false,
    total_pax: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [tableAvailabilitySelector, setTableAvailabilitySelector] = useState([
    {
      name: "All",
      count: 0,
      color: "bg-[#5C4BF5] border border-[#5C4BF5] text-white",
    },
    {
      name: "Available",
      count: 0,
      color: "bg-[#E9F9EF] border border-[#D3F3DF] text-[#1B9E4B]",
    },
    {
      name: "Full",
      count: 0,
      color: "bg-[#FEF6E6] border border-[#FCECCE] text-[#F0A009]",
    },
    {
      name: "Overflow",
      count: 0,
      color: "bg-[#FFEAEA] border border-[#FFD6D6] text-[#FF3030]",
    },
  ]);
  const [tableAvailability, setTableAvailability] = useState("All");

  const { toast } = useToast();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchTableList();
    }
  };

  const clearFilter = () => {
    setSearchTerm("");
    setIsSearchCleared(true);
  };

  const handlePageClick = (e) => {
    fetchTableList(e.selected + 1);
  };

  const handleChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchTableList(1, e.target.value);
  };

  const fetchWidgetData = useCallback(async () => {
    setLoading(true);
    try {
      axios
        .get(GET_SEATS_WIDGET_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            eventId: sessionStorage.getItem("event_id"),
          },
        })
        .then((response) => {
          setLoading(false);
          setWidgetData(response.data.data);
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
          toast("Failed to fetch widget data", "error");
        });
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast("Failed to fetch widget data", "error");
    }
  }, [toast]);

  const fetchAvailabilityCount = useCallback(async () => {
    setLoading(true);
    axios
      .get(GET_AVAILABILITY_COUNT_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setLoading(false);
        setTableAvailabilitySelector((prev) =>
          prev.map((item) => ({
            ...item,
            count: response.data.data[item.name.toLowerCase()] || 0,
          }))
        );
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch availability count.", "error");
      });
  }, [toast]);

  const fetchTableList = useCallback(async (page = 1, perPageValue = perPage) => {
    setLoading(true);
    axios
      .get(GET_TABLE_DETAIL_LIST_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
          search: searchTerm,
          page: page,
          perPage: perPageValue,
          availability: tableAvailability.toLowerCase(),
        },
      })
      .then((response) => {
        setLoading(false);
        setTableDetailData(response.data.data);
        setTableDetailMeta(response.data.meta.pagination);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to fetch check-in list", "error");
      });
  }, [perPage, searchTerm, tableAvailability, toast]);

  const handleSubmit = () => {
    setLoading(true);
    axios
      .post(
        CREATE_TABLE_API,
        { ...formData, event_id: sessionStorage.getItem("event_id") },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      )
      .then((response) => {
        toggleModal();
        setLoading(false);
        fetchTableList();
        fetchWidgetData();
        toast("Table created successfully", "success");
        setFormData({
          name: "",
          description: "",
          pax: 10,
          is_multi: false,
          total_pax: 0,
        });
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        toast("Failed to create table", "error");
      });
  };

  useEffect(() => {
    fetchWidgetData();
  }, [fetchWidgetData]);

  useEffect(() => {
    fetchTableList();
    fetchAvailabilityCount();
  }, [tableAvailability, fetchTableList, fetchAvailabilityCount]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchTableList();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchTableList]);

  const breadcrumbItems = [{ label: "Seat Management" }, { label: "List" }];

  return (
    <div className="flex flex-col w-full h-full">
      {/* Create modal */}
      {showModal && (
        <Modal
          title="Add Table"
          toggleModal={toggleModal}
          handleSubmit={handleSubmit}
        >
          <div className="flex flex-col w-full h-full gap-[24px]">
            {formData.is_multi ? (
              <div className="flex flex-col w-full h-full gap-[24px]">
                <TextboxSet
                  label="Total Guests Attending"
                  name="total_pax"
                  type="number"
                  value={formData.total_pax}
                  required
                  onChange={(e) => handleChange(e)}
                />
              </div>
            ) : (
              <div className="flex flex-col w-full h-full gap-[24px]">
                <TextboxSet
                  label="Table Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  required
                  onChange={(e) => handleChange(e)}
                />
                <Counter formData={formData} setFormData={setFormData} />
                <TextboxSet
                  label="Table Description"
                  name="description"
                  type="textarea"
                  value={formData.description}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            )}
            <div className="flex items-center gap-4">
              <p>Create multiple tables</p>
              <Switch
                checkedIcon={false}
                uncheckedIcon={false}
                offColor="#EFF0F2"
                onColor="#5C4BF5"
                width={48}
                height={28}
                checked={formData.is_multi}
                onChange={() => {
                  setFormData({
                    ...formData,
                    is_multi: !formData.is_multi,
                  });
                }}
              />
            </div>
          </div>
        </Modal>
      )}

      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[12px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} />

          {/* Title row */}
          <div className="flex flex-col md:flex-row w-full justify-between items-center">
            <h1 className="text-[29px]">Seats Management</h1>
            <Can permission="create_seat">
              <Button onClick={toggleModal}>Add Table</Button>
            </Can>
          </div>

          {/* Widgets */}
          {widgetData && (
            <div className="grid grid-cols-1 md:grid-cols-3 w-full gap-[12px] md:gap-[24px]">
              <WidgetCard name="Total Tables" data={widgetData.total_tables} />
              <WidgetCard name="Total Seats" data={widgetData.total_seats} />
              <WidgetCard
                name="Seats Assigned"
                data={widgetData.total_seats_assigned}
              />
            </div>
          )}

          {/* Table */}
          {tableDetailData && tableDetailMeta && (
            <SeatManagementTable
              data={tableDetailData}
              meta={tableDetailMeta}
              tableAvailabilitySelector={tableAvailabilitySelector}
              tableAvailability={tableAvailability}
              setTableAvailability={setTableAvailability}
              searchTerm={searchTerm}
              handlePageClick={handlePageClick}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
              clearFilter={clearFilter}
            />
          )}
        </div>
      </LoadingInComponent>
    </div>
  );
};

export default SeatManagementList;
